import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { RevealText, Emblem } from "./Primitives";
import { IMAGES } from "../data/content";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* atmospheric glows */}
      <div className="crimson-glow absolute -top-20 right-[8%] h-[520px] w-[520px] rounded-full" />
      <div className="crimson-glow absolute bottom-0 left-[-6%] h-[380px] w-[380px] rounded-full opacity-60" />

      {/* Statue image, right side, parallax */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-y-0 right-0 w-full md:w-[58%] lg:w-[50%] z-0"
      >
        <div className="relative h-full w-full">
          <img
            src={IMAGES.heroBust}
            alt="Classical marble bust dissolving into crimson neural veins"
            className="h-full w-full object-cover object-center"
            loading="eager"
          />
          {/* left-edge blend into black */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent md:via-[#050505]/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/30" />
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-10 min-h-[100svh] flex flex-col justify-center"
      >
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-crimson" style={{ background: "var(--noir-crimson)" }} />
            <span className="font-mono-accent text-[10px] md:text-xs uppercase tracking-[0.35em] text-white/50">
              The mind is the last frontier
            </span>
          </motion.div>

          <h1 className="font-display leading-[0.85] tracking-tight">
            <span className="block text-[19vw] md:text-[13vw] lg:text-[11rem] font-light">
              <RevealText delay={0.15}>NOIR</RevealText>
            </span>
            <span className="block text-[13vw] md:text-[8.5vw] lg:text-[7.5rem] text-crimson font-light tracking-[0.05em] pl-1">
              <RevealText delay={0.32}>PSYCHE</RevealText>
            </span>
          </h1>

          <div className="mt-8 flex items-center gap-4 text-white/70">
            <Emblem size={20} className="text-white/40 shrink-0" />
            <p className="font-display italic text-2xl md:text-4xl tracking-wide">
              <RevealText delay={0.5}>
                <span>
                  See What <span className="text-crimson not-italic">Others</span> Miss.
                </span>
              </RevealText>
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-videos"
              onClick={() =>
                document.getElementById("videos")?.scrollIntoView({ behavior: "smooth" })
              }
              className="group relative overflow-hidden bg-white text-black px-8 py-4 font-mono-accent text-[11px] uppercase tracking-[0.25em] transition-transform active:scale-[0.97]"
            >
              <span className="relative z-10 transition-colors group-hover:text-white">
                Watch the Films
              </span>
              <span className="absolute inset-0 z-0 origin-left scale-x-0 bg-[var(--noir-crimson)] transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
            </button>
            <button
              data-testid="hero-cta-subscribe"
              onClick={() =>
                document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth" })
              }
              className="px-8 py-4 font-mono-accent text-[11px] uppercase tracking-[0.25em] text-white/70 border border-white/15 hover:border-white/40 hover:text-white transition-colors"
            >
              Join the Circle
            </button>
          </motion.div>
        </div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-mono-accent text-[9px] uppercase tracking-[0.3em] text-white/40">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-white/40" />
        </motion.div>
      </motion.div>
    </section>
  );
}
